import { useState } from 'react';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import { useFindAccount } from '@features/auth/hooks/useFindAccount';
import { useResetPasswordMutation } from '@features/auth/hooks';
import validatePassword from '@features/user/utils/validatePassword';
import styles from '@pages/login/components/mobile/MobileFindAccount.module.scss';

export default function MobileFindAccount({
  mode: initialMode,
  companyList = [],
  onBack,
  onSuccess,
  onError,
  onResendSuccess,
}) {
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [findResult, setFindResult] = useState(null);

  // 비밀번호 재설정용 로컬 상태
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  const title =
    currentMode === 'findPassword'
      ? findResult
        ? '비밀번호 재설정'
        : '비밀번호 찾기'
      : '아이디 찾기';

  const { state, actions } = useFindAccount({
    mode: currentMode,
    onSuccess: (data) => {
      setFindResult(data.result?.resultData);
    },
    onError,
    onResendSuccess,
  });

  // 비밀번호 재설정 Mutation
  const { mutate: resetPassword, isPending: isResetting } = useResetPasswordMutation({
    onSuccess() {
      onSuccess?.({ mode: 'resetSuccess' });
      onBack(); // 성공 후 닫기
    },
    onError(error) {
      console.error('비밀번호 변경 실패:', error); // 에러 객체 확인
      const message = error?.response?.data?.message ?? '비밀번호 변경에 실패했습니다.';
      onError?.(message);
    },
  });

  const handleResetSubmit = () => {
    // 초기화
    setPasswordError('');
    setConfirmError('');

    if (!newPassword) {
      setPasswordError('비밀번호를 입력해주세요.');
      return;
    }

    if (!passwordConfirm) {
      setConfirmError('비밀번호 확인을 입력해주세요.');
      return;
    }

    if (newPassword !== passwordConfirm) {
      setConfirmError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!validatePassword(newPassword)) {
      setPasswordError('영문, 숫자, 특수문자를 포함해 8자 이상 입력해주세요.');
      return;
    }

    resetPassword({
      email: state.email,
      resetToken: findResult.resetToken,
      changePw: newPassword,
    });
  };

  // 결과 화면 (아이디 확인 또는 비밀번호 재설정)
  if (findResult) {
    return (
      <div className={styles.page}>
        <MobileModalHeader title={title} onBack={onBack} />
        <div className={styles.container}>
          {currentMode === 'findPassword' ? (
            <div className={styles.form}>
              <div className={styles.accountSection}>
                <div className={styles.descSection}>영문, 숫자, 특수문자 혼합 8자 이상 </div>
                <div className={styles.nameField}>
                  <input
                    type="password"
                    placeholder="새 비밀번호 (8자 이상)"
                    className={`${styles.input} ${passwordError ? styles.errorInput : ''}`}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                    }}
                  />
                  {passwordError && <p className={styles.errorText}>{passwordError}</p>}
                </div>
                <div className={styles.nameField}>
                  <input
                    type="password"
                    placeholder="새 비밀번호 확인"
                    className={`${styles.input} ${confirmError ? styles.errorInput : ''}`}
                    value={passwordConfirm}
                    onChange={(e) => {
                      setPasswordConfirm(e.target.value);
                      if (confirmError) setConfirmError('');
                    }}
                  />
                  {confirmError && <p className={styles.errorText}>{confirmError}</p>}
                </div>
              </div>
            </div>
          ) : (
            // 결과 출력
            <div className={styles.resultContent}>
              <div className={styles.resultBox}>
                <span className={styles.label}>아이디</span>
                {Array.isArray(findResult.user) &&
                  findResult.user.map((u, idx) => (
                    <span key={idx} className={styles.value}>
                      {u.id}
                    </span>
                  ))}
              </div>
              <div className={styles.subActionButtonArea}>
                <button
                  type="button"
                  className={styles.subActionButton}
                  onClick={() => {
                    setFindResult(null);
                    setCurrentMode('findPassword');
                    actions.reset();
                  }}
                >
                  비밀번호 찾기
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.primaryButton}`}
            onClick={currentMode === 'findPassword' ? handleResetSubmit : onBack}
            disabled={isResetting}
          >
            {currentMode === 'findPassword'
              ? isResetting
                ? '변경 중...'
                : '비밀번호 변경'
              : '로그인하러 가기'}
          </button>
        </div>
      </div>
    );
  }

  // 기본 입력 화면 (아이디/비밀번호 찾기 인증 단계)
  return (
    <div className={styles.page}>
      <MobileModalHeader title={title} onBack={onBack} />
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.accountSection}>
            {currentMode === 'findPassword' && (
              <>
                {/* 회사 선택 */}
                <div className={styles.nameField}>
                  <select
                    className={styles.input}
                    value={state.companyCode}
                    onChange={(e) => actions.setCompanyCode(e.target.value)}
                  >
                    <option value="">회사 선택</option>
                    {companyList.map((company) => (
                      <option key={company.companyCode} value={company.companyCode}>
                        {company.company}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.nameField}>
                  <input
                    type="text"
                    placeholder="로그인 아이디"
                    className={styles.input}
                    value={state.loginId}
                    onChange={(e) => actions.setLoginId(e.target.value)}
                  />
                </div>
              </>
            )}
            <div className={styles.nameField}>
              <input
                type="text"
                placeholder="이름"
                className={styles.input}
                value={state.name}
                onChange={(e) => actions.setName(e.target.value)}
              />
            </div>
            <div className={styles.emailAuthField}>
              <input
                type="text"
                placeholder="사내 이메일 주소"
                className={styles.input}
                value={state.email}
                onChange={(e) => actions.setEmail(e.target.value)}
              />
              <button
                type="button"
                className={styles.authButton}
                onClick={actions.handleSendCode}
                disabled={state.isSending || (state.resendRemaining > 0 && state.isCodeSent)}
              >
                {state.isSending ? '전송 중...' : state.isCodeSent ? '재전송' : '인증번호 받기'}
              </button>
            </div>
          </div>
          {state.isCodeSent && (
            <div className={styles.verifySection}>
              <input
                type="text"
                placeholder="인증번호 6자리"
                className={styles.input}
                value={state.code}
                onChange={(e) => actions.setCode(e.target.value)}
              />
              <div className={styles.timerSection}>
                <div className={`${styles.timer} ${state.remaining <= 0 ? styles.expired : ''}`}>
                  {state.remaining > 0
                    ? `남은 시간 ${actions.formatTime(state.remaining)}`
                    : '인증시간 만료'}
                </div>
                <div className={styles.resendText} onClick={actions.handleSendCode}>
                  {state.resendRemaining > 0 ? (
                    <span className={styles.resendWait}>재전송 ({state.resendRemaining}s)</span>
                  ) : (
                    '인증메일 재전송'
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.primaryButton}`}
          onClick={actions.handleVerify}
          disabled={state.isVerifying || !state.isCodeSent}
        >
          {state.isVerifying ? '확인 중...' : '확인'}
        </button>
      </div>
    </div>
  );
}
